import { Link } from "react-router-dom"
import { addDays, eachDayOfInterval, format, isSameDay, isToday, parseISO } from "date-fns"
import { cn } from "@/lib/utils"
import { isBlock, propertyColor, shortName } from "~/lib/calendar"
import CleanBadge from "~/components/calendar/CleanBadge"
import routes from "~/routes"

const AGENDA_DAYS = 60

function Dot({ property }) {
  return <span className="mr-1.5 inline-block size-2 shrink-0 rounded-full" style={{ background: propertyColor(property) }} />
}

// The mobile replacement for the month grid: one card per day in the next 60
// days that has something happening -- check-outs, then cleans, then
// check-ins, in the order the day actually unfolds. Blocks are skipped.
export default function Agenda({ properties, bookings, cleans }) {
  const propertyById = Object.fromEntries(properties.map((p) => [ p.id, p ]))
  const stays = bookings.filter((b) => !isBlock(b))
  const today = new Date()

  const rows = eachDayOfInterval({ start: today, end: addDays(today, AGENDA_DAYS) })
    .map((day) => ({
      day,
      checkouts: stays.filter((b) => isSameDay(parseISO(b.ends_on), day)),
      cleans: cleans.filter((c) => isSameDay(parseISO(c.scheduled_on), day)),
      checkins: stays.filter((b) => isSameDay(parseISO(b.starts_on), day)),
    }))
    .filter((r) => r.checkouts.length || r.cleans.length || r.checkins.length)

  if (rows.length === 0) {
    return <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">Nothing in the next {AGENDA_DAYS} days.</p>
  }

  return (
    <div className="space-y-3">
      {rows.map(({ day, checkouts, cleans: dayCleans, checkins }) => (
        <div key={day.toISOString()} className="rounded-xl border border-border bg-card p-3">
          <p className={cn("mb-2 text-xs font-bold tracking-wide uppercase", isToday(day) ? "text-brand" : "text-muted-foreground")}>
            {isToday(day) ? "Today — " : ""}
            {format(day, "EEE d MMM")}
          </p>

          <div className="space-y-1.5">
            {checkouts.map((b) => (
              <Link key={`out-${b.id}`} to={routes.bookingDetail(b.id)} className="flex items-center text-sm">
                <Dot property={propertyById[b.property_id]} />
                <span className="mr-1 text-muted-foreground">Check-out:</span>
                <span className="truncate font-medium">{b.guest_label}</span>
                <span className="ml-1 text-xs text-muted-foreground">({shortName(propertyById[b.property_id])})</span>
              </Link>
            ))}

            {dayCleans.map((c) => (
              <div key={`clean-${c.id}`} className="flex items-center text-sm">
                <Dot property={propertyById[c.property_id]} />
                <span className="mr-1 text-muted-foreground">Clean:</span>
                <CleanBadge clean={c} />
              </div>
            ))}

            {checkins.map((b) => (
              <Link key={`in-${b.id}`} to={routes.bookingDetail(b.id)} className="flex items-center text-sm">
                <Dot property={propertyById[b.property_id]} />
                <span className="mr-1 text-muted-foreground">{b.kind === "residency" ? "Residency check-in:" : "Check-in:"}</span>
                <span className="truncate font-medium">
                  {b.guest_label}
                  {b.guest_count ? ` ×${b.guest_count}` : ""}
                </span>
                <span className="ml-1 text-xs text-muted-foreground">({shortName(propertyById[b.property_id])})</span>
                {b.needs_enrichment && <span className="ml-1 text-xs font-medium text-amber-600 dark:text-amber-400">needs details</span>}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
